import { useState, useEffect, useId, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import { createCrmTeam, updateCrmTeam } from "../../../services/crmTeams";
// updateCrmTeam is used with UpdateCrmTeamRequest which supports defaultContactCoachId
import { createCrmContact, linkCoachToTeam } from "../../../services/crmContacts";
import { CoachTypeahead } from "../../../components/CoachTypeahead";
import type { CoachOption } from "../../../components/CoachTypeahead";
import { SelectPopover } from "../../../components/SelectPopover";
import { ComboboxPopover } from "../../../components/ComboboxPopover";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import { US_STATES } from "../../../constants/usStates";
import { DIVISIONS } from "../../../constants/divisions";
import { COACH_TITLES } from "../../../constants/coachTitles";
import {
  availablePreferredContactMethodLabels,
  preferredContactMethodLabel,
  preferredContactMethodValue,
} from "../../../constants/preferredContactMethods";
import "./CreateCrmTeamModal.css";

interface CreateCrmTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface NewCoachFormData {
  tempId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  xHandle: string;
  preferredContactMethod: string;
  roleTitle: string;
}

const emptyCoachForm = (tempId: string): NewCoachFormData => ({
  tempId,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  xHandle: "",
  preferredContactMethod: "",
  roleTitle: "",
});

export const CreateCrmTeamModal = ({ isOpen, onClose, onSuccess }: CreateCrmTeamModalProps) => {
  const uid = useId();

  // School fields
  const [name, setName] = useState("");
  const [division, setDivision] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [conference, setConference] = useState("");
  const [region, setRegion] = useState("");
  const [teamTwitter, setTeamTwitter] = useState("");
  const [notes, setNotes] = useState("");

  // Coaches
  const [pendingCoaches, setPendingCoaches] = useState<NewCoachFormData[]>([]);
  const [showNewCoachForm, setShowNewCoachForm] = useState(false);
  const [draftCoach, setDraftCoach] = useState<NewCoachFormData>(emptyCoachForm("draft"));
  const [linkedCoaches, setLinkedCoaches] = useState<CoachOption[]>([]);
  const [defaultContactKey, setDefaultContactKey] = useState<string>("");

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDivision("");
      setState("");
      setCity("");
      setConference("");
      setRegion("");
      setTeamTwitter("");
      setNotes("");
      setPendingCoaches([]);
      setShowNewCoachForm(false);
      setDraftCoach(emptyCoachForm("draft"));
      setLinkedCoaches([]);
      setDefaultContactKey("");
      setError("");
      setSaving(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !saving) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, saving]);

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  // Merged list of all coaches in the form (pending + linked), used for default contact selector
  const allCoaches = [
    ...pendingCoaches.map((c) => ({ key: `new:${c.tempId}`, label: `${c.firstName} ${c.lastName}`.trim() || "(unnamed)" })),
    ...linkedCoaches.map((c) => ({ key: `linked:${c.id}`, label: c.displayName })),
  ];

  const hasCoaches = allCoaches.length > 0;
  const isValidState = US_STATES.includes(state.toUpperCase());

  const isSaveDisabled =
    saving ||
    !name.trim() ||
    !division ||
    !state.trim() ||
    !isValidState ||
    (hasCoaches && !defaultContactKey);

  const handleAddPendingCoach = () => {
    const validationError = validateDraftCoach(draftCoach);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setPendingCoaches((prev) => [...prev, { ...draftCoach, tempId: `${Date.now()}` }]);
    setDraftCoach(emptyCoachForm("draft"));
    setShowNewCoachForm(false);
  };

  const handleRemovePendingCoach = (tempId: string) => {
    setPendingCoaches((prev) => prev.filter((c) => c.tempId !== tempId));
    if (defaultContactKey === `new:${tempId}`) setDefaultContactKey("");
  };

  const handleRemoveLinkedCoach = (id: string) => {
    setLinkedCoaches((prev) => prev.filter((c) => c.id !== id));
    if (defaultContactKey === `linked:${id}`) setDefaultContactKey("");
  };

  const handleLinkCoach = (coach: CoachOption | null) => {
    if (!coach) return;
    if (linkedCoaches.some((c) => c.id === coach.id)) return;
    setLinkedCoaches((prev) => [...prev, coach]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("School name is required"); return; }
    if (!division) { setError("Division is required"); return; }
    if (!state.trim()) { setError("State is required"); return; }
    if (!US_STATES.includes(state.toUpperCase())) { setError("Please enter a valid US state abbreviation"); return; }
    if (hasCoaches && !defaultContactKey) { setError("Please select a default contact"); return; }

    setError("");
    setSaving(true);

    try {
      // 1. Create the school
      const school = await createCrmTeam({
        name: name.trim(),
        division,
        state: state.trim(),
        city: city.trim() || undefined,
        conference: conference.trim() || undefined,
        region: region.trim() || undefined,
        teamTwitter: teamTwitter.trim() || undefined,
        notes: notes || undefined,
      });
      const schoolId = school.id;

      // 2. Create pending (new) coaches with the school's team ID
      const createdCoachIds: string[] = [];
      for (const coach of pendingCoaches) {
        const created = await createCrmContact({
          contactType: "Coach",
          firstName: coach.firstName,
          lastName: coach.lastName,
          email: coach.email || undefined,
          phone: coach.phone || undefined,
          xHandle: coach.xHandle || undefined,
          roleTitle: coach.roleTitle || undefined,
          preferredContactMethod: coach.preferredContactMethod || undefined,
          crmTeamId: schoolId,
        });
        createdCoachIds.push(created.id);
      }

      // 3. Link existing coaches to the school
      for (const coach of linkedCoaches) {
        await linkCoachToTeam(coach.id, schoolId);
      }

      // 4. Set default contact if selected
      if (defaultContactKey && hasCoaches) {
        let defaultCoachId: string | null = null;

        if (defaultContactKey.startsWith("new:")) {
          const tempId = defaultContactKey.slice(4);
          const idx = pendingCoaches.findIndex((c) => c.tempId === tempId || `new:${c.tempId}` === defaultContactKey);
          if (idx !== -1 && idx < createdCoachIds.length) {
            defaultCoachId = createdCoachIds[idx];
          }
        } else if (defaultContactKey.startsWith("linked:")) {
          defaultCoachId = defaultContactKey.slice(7);
        }

        if (defaultCoachId) {
          await updateCrmTeam(schoolId, {
            name: name.trim(),
            division,
            state: state.trim(),
            notes: notes || undefined,
            defaultContactCoachId: defaultCoachId,
          });
        }
      }

      onSuccess();
    } catch {
      setError("Failed to create school. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const updateDraftField = (field: keyof NewCoachFormData, value: string) => {
    setDraftCoach((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-clear preferred method if the backing field is cleared
      if (field === "email" && !value && next.preferredContactMethod === "Gmail") next.preferredContactMethod = "";
      if (field === "phone" && !value && next.preferredContactMethod === "Quo") next.preferredContactMethod = "";
      if (field === "xHandle" && !value && next.preferredContactMethod === "X") next.preferredContactMethod = "";
      return next;
    });
  };

  return (
    <div className="create-crm-team-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div ref={dialogRef} tabIndex={-1} className="create-crm-team-modal create-crm-team-modal--expanded" role="dialog" aria-modal="true" aria-label="Create School">
        <div className="create-crm-team-modal__header">
          <h2 className="create-crm-team-modal__title">New School</h2>
          <ModalCloseButton onClick={onClose} disabled={saving} />
        </div>

        <form className="create-crm-team-modal__body" onSubmit={handleSubmit}>
          {/* School info */}
          <div className="create-crm-team-modal__field">
            <label className="create-crm-team-modal__label" htmlFor={`${uid}-name`}>School Name *</label>
            <input
              id={`${uid}-name`}
              className="create-crm-team-modal__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Stanford"
              required
            />
          </div>

          <div className="create-crm-team-modal__row create-crm-team-modal__row--dropdowns">
            <div className="create-crm-team-modal__field">
              <label htmlFor="create-crm-team-modal-division-0" className="create-crm-team-modal__label">Division *</label>
              <SelectPopover id="create-crm-team-modal-division-0" options={DIVISIONS} value={division} onChange={setDivision} placeholder="Select..." fullWidth />
            </div>
            <div className="create-crm-team-modal__field">
              <label htmlFor="create-crm-team-modal-state-1" className="create-crm-team-modal__label">State *</label>
              <ComboboxPopover id="create-crm-team-modal-state-1" options={US_STATES} value={state} onChange={setState} placeholder="e.g. CA" transform={(v) => v.toUpperCase()} fullWidth />
            </div>
          </div>

          <div className="create-crm-team-modal__row">
            <div className="create-crm-team-modal__field">
              <label className="create-crm-team-modal__label" htmlFor={`${uid}-city`}>City</label>
              <input
                id={`${uid}-city`}
                className="create-crm-team-modal__input"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Palo Alto"
              />
            </div>
            <div className="create-crm-team-modal__field">
              <label className="create-crm-team-modal__label" htmlFor={`${uid}-conference`}>Conference</label>
              <input
                id={`${uid}-conference`}
                className="create-crm-team-modal__input"
                type="text"
                value={conference}
                onChange={(e) => setConference(e.target.value)}
                placeholder="e.g. Pac-12"
              />
            </div>
          </div>

          <div className="create-crm-team-modal__row">
            <div className="create-crm-team-modal__field">
              <label className="create-crm-team-modal__label" htmlFor={`${uid}-region`}>Region</label>
              <input
                id={`${uid}-region`}
                className="create-crm-team-modal__input"
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. West"
              />
            </div>
            <div className="create-crm-team-modal__field">
              <label className="create-crm-team-modal__label" htmlFor={`${uid}-twitter`}>Team Twitter</label>
              <input
                id={`${uid}-twitter`}
                className="create-crm-team-modal__input"
                type="text"
                value={teamTwitter}
                onChange={(e) => setTeamTwitter(e.target.value)}
                placeholder="@handle"
              />
            </div>
          </div>

          <div className="create-crm-team-modal__field">
            <label className="create-crm-team-modal__label" htmlFor={`${uid}-notes`}>Notes</label>
            <textarea
              id={`${uid}-notes`}
              className="create-crm-team-modal__input create-crm-team-modal__textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Coaches section */}
          <div className="create-crm-team-modal__section">
            <div className="create-crm-team-modal__section-header">
              <span className="create-crm-team-modal__section-title">Coaches</span>
              <span className="create-crm-team-modal__section-hint">(optional)</span>
            </div>

            {/* Link existing coaches */}
            <div className="create-crm-team-modal__field create-crm-team-modal__field--typeahead">
              <label htmlFor="create-crm-team-modal-link-existing-coach-0" className="create-crm-team-modal__label">Link existing coach</label>
              <CoachTypeahead inputId="create-crm-team-modal-link-existing-coach-0" value={null} onSelect={handleLinkCoach} placeholder="Search existing coaches..." />
            </div>

            {/* Linked coach chips */}
            {linkedCoaches.length > 0 && (
              <ul className="create-crm-team-modal__coach-list">
                {linkedCoaches.map((c) => (
                  <li key={c.id} className="create-crm-team-modal__coach-chip">
                    <span>{c.displayName}</span>
                    <button type="button" className="create-crm-team-modal__coach-chip-remove" onClick={() => handleRemoveLinkedCoach(c.id)} aria-label={`Remove ${c.displayName}`}>×</button>
                  </li>
                ))}
              </ul>
            )}

            {/* Pending new coaches list */}
            {pendingCoaches.length > 0 && (
              <ul className="create-crm-team-modal__coach-list">
                {pendingCoaches.map((c) => (
                  <li key={c.tempId} className="create-crm-team-modal__coach-chip create-crm-team-modal__coach-chip--new">
                    <span>{`${c.firstName} ${c.lastName}`.trim() || "(unnamed)"}</span>
                    <button type="button" className="create-crm-team-modal__coach-chip-remove" onClick={() => handleRemovePendingCoach(c.tempId)} aria-label="Remove coach">×</button>
                  </li>
                ))}
              </ul>
            )}

            {/* Inline new coach form */}
            {showNewCoachForm ? (
              <div className="create-crm-team-modal__coach-form">
                <div className="create-crm-team-modal__coach-form-title">New Coach</div>
                <div className="create-crm-team-modal__row">
                  <div className="create-crm-team-modal__field">
                    <label htmlFor="create-crm-team-modal-first-name-0" className="create-crm-team-modal__label">First Name *</label>
                    <input id="create-crm-team-modal-first-name-0" className="create-crm-team-modal__input" type="text" value={draftCoach.firstName} onChange={(e) => updateDraftField("firstName", e.target.value)} placeholder="First" />
                  </div>
                  <div className="create-crm-team-modal__field">
                    <label htmlFor="create-crm-team-modal-last-name-1" className="create-crm-team-modal__label">Last Name *</label>
                    <input id="create-crm-team-modal-last-name-1" className="create-crm-team-modal__input" type="text" value={draftCoach.lastName} onChange={(e) => updateDraftField("lastName", e.target.value)} placeholder="Last" />
                  </div>
                </div>
                <div className="create-crm-team-modal__row">
                  <div className="create-crm-team-modal__field">
                    <label htmlFor="create-crm-team-modal-email-2" className="create-crm-team-modal__label">Email</label>
                    <input id="create-crm-team-modal-email-2" className="create-crm-team-modal__input" type="email" value={draftCoach.email} onChange={(e) => updateDraftField("email", e.target.value)} placeholder="coach@school.edu" />
                  </div>
                  <div className="create-crm-team-modal__field">
                    <label htmlFor="create-crm-team-modal-phone-3" className="create-crm-team-modal__label">Phone</label>
                    <input id="create-crm-team-modal-phone-3" className="create-crm-team-modal__input" type="tel" value={draftCoach.phone} onChange={(e) => updateDraftField("phone", e.target.value)} placeholder="555-000-0000" />
                  </div>
                </div>
                <div className="create-crm-team-modal__row">
                  <div className="create-crm-team-modal__field">
                    <label htmlFor="create-crm-team-modal-x-handle-4" className="create-crm-team-modal__label">X Handle</label>
                    <input id="create-crm-team-modal-x-handle-4" className="create-crm-team-modal__input" type="text" value={draftCoach.xHandle} onChange={(e) => updateDraftField("xHandle", e.target.value)} placeholder="@handle" />
                  </div>
                  <div className="create-crm-team-modal__field">
                    <label htmlFor="create-crm-team-modal-preferred-contact-5" className="create-crm-team-modal__label">Preferred Contact</label>
                    <SelectPopover
                      id="create-crm-team-modal-preferred-contact-5"
                      options={availablePreferredContactMethodLabels(draftCoach.email, draftCoach.phone, draftCoach.xHandle)}
                      value={preferredContactMethodLabel(draftCoach.preferredContactMethod)}
                      onChange={(label) => updateDraftField("preferredContactMethod", preferredContactMethodValue(label) ?? "")}
                      placeholder="Select..."
                      fullWidth
                    />
                  </div>
                </div>
                <div className="create-crm-team-modal__field">
                  <label htmlFor="create-crm-team-modal-title-role-6" className="create-crm-team-modal__label">Title / Role</label>
                  <SelectPopover
                    id="create-crm-team-modal-title-role-6"
                    options={COACH_TITLES}
                    value={draftCoach.roleTitle}
                    onChange={(v) => updateDraftField("roleTitle", v)}
                    placeholder="Select..."
                    fullWidth
                  />
                </div>
                <div className="create-crm-team-modal__coach-form-actions">
                  <button type="button" className="create-crm-team-modal__coach-form-cancel" onClick={() => { setShowNewCoachForm(false); setDraftCoach(emptyCoachForm("draft")); setError(""); }}>
                    Cancel
                  </button>
                  <button type="button" className="create-crm-team-modal__coach-form-add" onClick={handleAddPendingCoach}>
                    Add Coach
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="create-crm-team-modal__add-coach-btn" onClick={() => { setShowNewCoachForm(true); setError(""); }}>
                + Create new coach
              </button>
            )}

            {/* Default contact selector — only shown when coaches have been added */}
            {hasCoaches && (
              <div className="create-crm-team-modal__field create-crm-team-modal__field--default-contact">
                <label htmlFor="create-crm-team-modal-default-contact-7" className="create-crm-team-modal__label">Default Contact *</label>
                <select id="create-crm-team-modal-default-contact-7"
                  className="create-crm-team-modal__input"
                  value={defaultContactKey}
                  onChange={(e) => setDefaultContactKey(e.target.value)}
                  required
                >
                  <option value="">Select default contact...</option>
                  {allCoaches.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
                <p className="create-crm-team-modal__hint">Used for mass outreach to this school.</p>
              </div>
            )}
          </div>

          {error && <div className="create-crm-team-modal__error">{error}</div>}

          <div className="create-crm-team-modal__footer">
            <button type="button" className="create-crm-team-modal__cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="create-crm-team-modal__save" disabled={isSaveDisabled}>
              {saving ? "Creating..." : "Create School"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function validateDraftCoach(c: NewCoachFormData): string | null {
  if (!c.firstName.trim() || !c.lastName.trim()) return "Coach first and last name are required";
  if (!c.email.trim() && !c.phone.trim() && !c.xHandle.trim())
    return "Enter at least one of: email, phone, or X handle for the coach";
  return null;
}
